export const betaPrograms = [
  {
    slug: 'ordering-samples',
    name: 'Ordering + Samples',
    description:
      'Preview sample-ordering capabilities directly inside the full Mirage site experience.',
    status: 'alpha',
    extraNav: [{ label: 'SAMPLES', href: '/samples' }],
    intention:
      'Test a full commerce-adjacent workflow that covers sample ordering, checkout simulation, fulfillment handoff, and outbound communication.',
    differences: [
      'Adds sample-ordering flow inside the core site experience.',
      'Includes sample checkout simulation with fulfillment-routing preview.',
      'Keeps all navigation within this beta namespace for complete end-to-end demos.'
    ],
    features: {
      sampleCatalog: true,
      sampleOrderingFlow: true,
      stripeCheckout: 'simulated',
      fbaFulfillment: 'simulated'
    }
  },
  {
    slug: 'email-generator',
    name: 'Email Generator',
    description:
      'Preview automated operational email creation with recipient preload, generated attachments, and send simulation.',
    status: 'alpha',
    extraNav: [{ label: 'EMAIL GENERATOR', href: '/email-generator' }],
    intention:
      'Show how Mirage can prepare and send repeatable communication packs for GFI and Mitsui without manual drafting.',
    differences: [
      'Provides prebuilt templates for BOL confirmation, final release, and weekly readiness updates.',
      'Preloads recipient groups (14 GFI or 6 Mitsui) and simulates attachments.',
      'Supports one-click draft generation for internal review and send testing.'
    ],
    features: {
      emailAutomation: true
    }
  },
  {
    slug: 'document-upload-sort',
    name: 'Document Upload + Sort',
    description:
      'Preview an AI-first document intake pipeline: upload from email, auto-sort document type, extract fields, and publish to dashboards.',
    status: 'alpha',
    extraNav: [{ label: 'DOCUMENT FLOW', href: '/document-flow' }],
    intention:
      'Demonstrate how incoming operations documents can move from email attachments into structured data and dashboard visibility.',
    differences: [
      'Visualizes document lifecycle from inbox attachment to dashboard update.',
      'Auto-classifies document type before extraction and database write.',
      'Shows extracted fields and downstream dashboard payload in a single workflow.'
    ],
    features: {
      documentFlow: true
    }
  },
  {
    slug: 'seo-strategy',
    name: 'SEO Strategy + Content',
    description:
      'Preview route-level SEO improvements and a phased implementation plan integrated into the full site.',
    status: 'alpha',
    extraNav: [
      { label: 'BETA FEATURES', href: '/features' },
      { label: 'SEO LAB', href: '/seo-lab' }
    ],
    intention:
      'Show how SEO recommendations can be embedded into each route and rolled out through a phased execution plan.',
    differences: [
      'Displays route-level SEO highlights while navigating the site.',
      'Adds an SEO Lab with implementation phases and KPI tracking.',
      'Frames content decisions around commercial-intent keywords and conversion outcomes.'
    ],
    features: {
      seoLab: true,
      seoHighlights: true
    }
  },
  {
    slug: 'bidor-style-catalog',
    name: 'Bidor-Style Mix Catalog',
    description:
      'Preview a Bridor-inspired catalog layout that combines pastry and bakery mixes into one searchable grid.',
    status: 'alpha',
    extraNav: [{ label: 'BETA FEATURES', href: '/features' }],
    intention:
      'Evaluate a high-density catalog browsing experience where all mixes are shown on one page with filter-first navigation.',
    differences: [
      'Combines pastry and bakery mixes into one unified catalog page.',
      'Uses a sidebar filter + product grid pattern inspired by Bridor layout style.',
      'Emphasizes quick scanning with reference IDs, concise specs, and badge-based status.'
    ],
    referenceUrl: 'https://america.bridor.com/en/products/industries/cruise-lines',
    features: {
      bidorCatalog: true
    }
  },
  {
    slug: 'easy-manage-admin',
    name: 'Easy Manage Admin',
    description:
      'Preview a lightweight admin workflow for adding, editing, and removing pastry and bakery mixes directly in-browser for this beta session.',
    status: 'alpha',
    extraNav: [
      { label: 'BETA FEATURES', href: '/features' },
      { label: 'MANAGE MIXES', href: '/admin' }
    ],
    intention:
      'Demonstrate how Mirage teams could quickly manage mix content during reviews without waiting on backend development.',
    differences: [
      'Adds a dedicated Easy Manage Admin page for creating, editing, and removing pastry and bakery mixes in-session.',
      'Managed mix changes appear immediately on pastry and bakery pages in this beta version.',
      'Changes are intentionally temporary and reset on full browser refresh.'
    ],
    features: {
      easyManageAdmin: true,
      easyAddAdmin: true
    }
  },
  {
    slug: 'production-calendar',
    name: 'Production Calendar',
    description:
      'Preview a spreadsheet-driven production planning workspace with editable monthly quantities and ingredient inventory tracking.',
    status: 'alpha',
    extraNav: [
      { label: 'BETA FEATURES', href: '/features' },
      { label: 'PRODUCTION CALENDAR', href: '/production-calendar' },
      { label: 'PRODUCTION ESTIMATOR', href: '/production-estimator' }
    ],
    intention:
      'Turn the 2026 production workbook into an interactive planning screen where operations can adjust output and inventory assumptions in real time.',
    differences: [
      'Hardcodes formulas and baseline values extracted from the January-December 2026 workbook snapshot.',
      'Lets users edit monthly case quantities by product and immediately recalculate ingredient requirements.',
      'Tracks inventory changes in-session and surfaces projected shortages for rapid planning iterations.',
      'Adds a location-based production estimator page for entering GFI weekly data, BOL inputs, and extra-stock coverage targets.'
    ],
    features: {
      productionCalendar: true,
      productionEstimator: true
    }
  }
];

export const betaProgramBySlug = betaPrograms.reduce((accumulator, program) => {
  accumulator[program.slug] = program;
  return accumulator;
}, {});

export const getBetaProgram = (slug) => {
  if (!slug) {
    return null;
  }

  const aliasSlug = slug === 'easy-add-admin' ? 'easy-manage-admin' : slug;
  return betaProgramBySlug[aliasSlug] || null;
};
